import * as React from "react";
import {
  Alert,
  Box,
  Breadcrumbs,
  Checkbox,
  CircularProgress,
  IconButton,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  extractPartsPayload,
  formatOemRef,
  loadOemDiagramNav,
  normalizeOemRef,
  OemDiagramNavContext,
  OemDiagramSibling,
  partName,
  partNumber,
  partRef,
  saveOemDiagramNav,
  VinalfaHotspot,
  VinalfaPart,
} from "../../interfaces/Vinalfa";
import { fetchVinalfaSectionParts } from "../../services/vinalfaService";
import theme from "../../theme";

type Props = {
  sectionHash: string;
  oemTitle?: string | null;
  oemParent?: string | null;
  vehicleHash?: string | null;
  linkageTargetId?: string | null;
  linkageTargetType?: string | null;
  /** Full-width embed on /assemblies (no lg maxWidth) */
  embedded?: boolean;
  onOemClick?: (oem: string) => void;
  onSectionChange?: (sectionHash: string, oemTitle: string) => void;
  onBackToCards?: () => void;
};

const VinalfaOemDiagramSection = ({
  sectionHash,
  oemTitle,
  oemParent,
  vehicleHash,
  linkageTargetId,
  linkageTargetType,
  embedded = false,
  onOemClick,
  onSectionChange,
  onBackToCards,
}: Props) => {
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [image, setImage] = React.useState<string | undefined>();
  const [hotspots, setHotspots] = React.useState<VinalfaHotspot[]>([]);
  const [parts, setParts] = React.useState<VinalfaPart[]>([]);
  const [zoom, setZoom] = React.useState(1);
  const [selectedRefs, setSelectedRefs] = React.useState<Set<string>>(
    () => new Set()
  );
  const [activeRef, setActiveRef] = React.useState<string | null>(null);
  const [imgSize, setImgSize] = React.useState<{ w: number; h: number } | null>(
    null
  );
  const [nav, setNav] = React.useState<OemDiagramNavContext | null>(null);
  const rowRefs = React.useRef<Record<string, HTMLTableRowElement | null>>({});
  const listContainerRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    const stored = loadOemDiagramNav();
    if (stored) {
      const idx = stored.siblings.findIndex((s) => s.hash === sectionHash);
      setNav({
        ...stored,
        parentLabel: oemParent || stored.parentLabel,
        currentIndex: idx >= 0 ? idx : stored.currentIndex,
      });
    } else {
      setNav({
        parentLabel: oemParent || undefined,
        siblings: [{ hash: sectionHash, title: oemTitle || "Diagram" }],
        currentIndex: 0,
      });
    }
  }, [sectionHash, oemTitle, oemParent]);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      setActiveRef(null);
      setSelectedRefs(new Set());
      setZoom(1);
      setImgSize(null);
      try {
        const response = await fetchVinalfaSectionParts(sectionHash);
        if (cancelled) return;
        const parsed = extractPartsPayload(response);
        setImage(parsed.image);
        setHotspots(parsed.hotspots);
        setParts(parsed.parts);
      } catch (err: any) {
        if (!cancelled) {
          const detail =
            err?.response?.data?.detail || "Failed to load OEM diagram.";
          setError(String(detail));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sectionHash]);

  const scrollToRef = React.useCallback((refKey: string) => {
    const normalized = normalizeOemRef(refKey);
    const row =
      rowRefs.current[normalized] ||
      rowRefs.current[refKey] ||
      Object.entries(rowRefs.current).find(
        ([k]) => normalizeOemRef(k) === normalized
      )?.[1];
    if (row) {
      row.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, []);

  const activateRef = React.useCallback(
    (refRaw: string) => {
      const key = normalizeOemRef(refRaw) || refRaw;
      setActiveRef(key);
      // Defer scroll until row highlight paints
      requestAnimationFrame(() => scrollToRef(key));
    },
    [scrollToRef]
  );

  const toggleRef = (ref: string) => {
    const key = normalizeOemRef(ref) || ref;
    setSelectedRefs((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const isRowActive = (ref: string) => {
    const key = normalizeOemRef(ref) || ref;
    return activeRef === key || selectedRefs.has(key);
  };

  const visibleParts =
    selectedRefs.size > 0
      ? parts.filter((p) => selectedRefs.has(normalizeOemRef(partRef(p))))
      : parts;

  const goToSibling = (sibling: OemDiagramSibling, index: number) => {
    const nextNav: OemDiagramNavContext = {
      ...(nav || { siblings: [], currentIndex: 0 }),
      siblings: nav?.siblings?.length
        ? nav.siblings
        : [{ hash: sectionHash, title: oemTitle || "Diagram" }],
      currentIndex: index,
      parentLabel: oemParent || nav?.parentLabel,
    };
    saveOemDiagramNav(nextNav);
    setNav(nextNav);
    onSectionChange?.(sibling.hash, sibling.title);
  };

  const prevSibling =
    nav && nav.currentIndex > 0 ? nav.siblings[nav.currentIndex - 1] : null;
  const nextSibling =
    nav && nav.currentIndex < nav.siblings.length - 1
      ? nav.siblings[nav.currentIndex + 1]
      : null;

  const assembliesUrl = React.useMemo(() => {
    const params = new URLSearchParams();
    if (linkageTargetId) params.set("linkageTargetId", linkageTargetId);
    if (linkageTargetType) params.set("linkageTargetType", linkageTargetType);
    if (vehicleHash) params.set("vehicleHash", vehicleHash);
    const q = params.toString();
    return q ? `/assemblies?${q}` : "/assemblies";
  }, [linkageTargetId, linkageTargetType, vehicleHash]);

  const hotspotStyle = (hs: VinalfaHotspot, i: number) => {
    const ref = String(hs.ref ?? hs.number ?? i);
    const left = hs.left ?? hs.x;
    const top = hs.top ?? hs.y;
    if (left == null || top == null) return null;
    const w = hs.width;
    const h = hs.height;
    // Pixel coords relative to natural image size → % when we know dimensions
    const usePct =
      imgSize && imgSize.w > 0 && imgSize.h > 0 && (left > 100 || top > 100);
    const leftCss = usePct
      ? `${(Number(left) / imgSize!.w) * 100}%`
      : left <= 100 && top <= 100
        ? `${left}%`
        : left;
    const topCss = usePct
      ? `${(Number(top) / imgSize!.h) * 100}%`
      : left <= 100 && top <= 100
        ? `${top}%`
        : top;
    const widthCss =
      usePct && w != null ? `${(Number(w) / imgSize!.w) * 100}%` : undefined;
    const heightCss =
      usePct && h != null ? `${(Number(h) / imgSize!.h) * 100}%` : undefined;
    return { ref, leftCss, topCss, widthCss, heightCss };
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="warning" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        mb: embedded ? 0 : 2,
        width: "100%",
        maxWidth: embedded ? "100%" : (t) => t.breakpoints.values.lg,
        mx: embedded ? 0 : "auto",
        border: "1px solid #e0e0e0",
        borderRadius: 1,
        overflow: "hidden",
        bgcolor: "#fff",
        height: embedded ? "100%" : undefined,
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.25,
          borderBottom: "1px solid #eee",
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Breadcrumbs
          separator="›"
          aria-label="OEM breadcrumb"
          sx={{
            fontFamily: "Inter",
            fontSize: 13,
            "& .MuiBreadcrumbs-separator": { mx: 0.5 },
          }}
        >
          <Link
            href="/"
            underline="hover"
            color="inherit"
            sx={{ color: theme.palette.text.primary, fontSize: 13 }}
          >
            Search
          </Link>
          <Link
            href={assembliesUrl}
            underline="hover"
            color="inherit"
            sx={{ color: theme.palette.text.primary, fontSize: 13 }}
          >
            {nav?.vehicleLabel || "Vehicle"}
          </Link>
          {(oemParent || nav?.parentLabel) &&
            (embedded && onBackToCards ? (
              <Link
                component="button"
                type="button"
                underline="hover"
                onClick={onBackToCards}
                sx={{
                  color: theme.palette.text.primary,
                  fontSize: 13,
                  cursor: "pointer",
                  border: "none",
                  background: "none",
                  fontFamily: "Inter",
                }}
              >
                {oemParent || nav?.parentLabel}
              </Link>
            ) : (
              <Link
                href={assembliesUrl}
                underline="hover"
                color="inherit"
                sx={{ color: theme.palette.text.primary, fontSize: 13 }}
              >
                {oemParent || nav?.parentLabel}
              </Link>
            ))}
          <Typography
            color={theme.palette.primary.main}
            sx={{ fontSize: 13, fontWeight: 600, fontFamily: "Inter" }}
          >
            {oemTitle || "Diagram"}
          </Typography>
        </Breadcrumbs>

        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <IconButton
            size="small"
            disabled={!prevSibling}
            onClick={() =>
              prevSibling && goToSibling(prevSibling, (nav?.currentIndex || 0) - 1)
            }
            aria-label="previous diagram"
            sx={{
              border: "1px solid #ddd",
              borderRadius: 1,
              color: theme.palette.primary.main,
            }}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <Typography
            variant="caption"
            sx={{ fontFamily: "Inter", color: theme.palette.text.primary, px: 0.5 }}
          >
            {(nav?.currentIndex ?? 0) + 1}/{nav?.siblings?.length || 1}
          </Typography>
          <IconButton
            size="small"
            disabled={!nextSibling}
            onClick={() =>
              nextSibling && goToSibling(nextSibling, (nav?.currentIndex || 0) + 1)
            }
            aria-label="next diagram"
            sx={{
              border: "1px solid #ddd",
              borderRadius: 1,
              color: theme.palette.primary.main,
            }}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <Typography
        variant="h6"
        sx={{
          fontFamily: "Inter",
          fontWeight: 700,
          color: theme.palette.primary.main,
          textTransform: "uppercase",
          px: 2,
          pt: 1.5,
          pb: 1,
          fontSize: { xs: 15, md: 18 },
        }}
      >
        {oemTitle || "Diagram"}
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 0,
          alignItems: "stretch",
          borderTop: "1px solid #eee",
        }}
      >
        <Box
          sx={{
            flex: { md: "0 0 50%" },
            p: 1,
            position: "relative",
            bgcolor: "#fafafa",
            borderRight: { md: "1px solid #eee" },
            minHeight: 280,
          }}
        >
          <Box sx={{ display: "flex", gap: 0.5, mb: 1 }}>
            <IconButton
              size="small"
              onClick={() => setZoom((z) => Math.max(0.5, z - 0.15))}
              aria-label="zoom out"
            >
              <RemoveIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => setZoom((z) => Math.min(3, z + 0.15))}
              aria-label="zoom in"
            >
              <AddIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => setZoom(1)}
              aria-label="reset zoom"
            >
              <RestartAltIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box
            sx={{
              overflow: "auto",
              maxHeight: 420,
              position: "relative",
            }}
          >
            <Box
              sx={{
                transform: `scale(${zoom})`,
                transformOrigin: "top left",
                position: "relative",
                display: "inline-block",
                width: "100%",
              }}
            >
              {image ? (
                <Box
                  component="img"
                  src={image}
                  alt={oemTitle || "OEM diagram"}
                  onLoad={(e) => {
                    const el = e.currentTarget;
                    setImgSize({
                      w: el.naturalWidth || el.width,
                      h: el.naturalHeight || el.height,
                    });
                  }}
                  sx={{ width: "100%", display: "block" }}
                />
              ) : (
                <Typography
                  variant="body2"
                  sx={{ p: 4, textAlign: "center", color: theme.palette.text.primary }}
                >
                  No diagram image available.
                </Typography>
              )}
              {hotspots.map((hs, i) => {
                const style = hotspotStyle(hs, i);
                if (!style) return null;
                const active = isRowActive(style.ref);
                return (
                  <Box
                    key={`${style.ref}-${i}`}
                    onClick={() => activateRef(style.ref)}
                    sx={{
                      position: "absolute",
                      left: style.leftCss,
                      top: style.topCss,
                      width: style.widthCss,
                      height: style.heightCss,
                      minWidth: style.widthCss ? undefined : 22,
                      minHeight: style.heightCss ? undefined : 22,
                      transform: style.widthCss
                        ? undefined
                        : "translate(-50%, -50%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: active
                        ? theme.palette.secondary.main
                        : theme.palette.primary.main,
                      color: "#fff",
                      fontSize: 10,
                      fontWeight: 700,
                      borderRadius: style.widthCss ? 0.5 : "50%",
                      cursor: "pointer",
                      lineHeight: 1,
                      border: active
                        ? `2px solid ${theme.palette.primary.dark}`
                        : "1px solid rgba(255,255,255,0.5)",
                      boxSizing: "border-box",
                      zIndex: 2,
                    }}
                  >
                    {formatOemRef(style.ref)}
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>

        <Box sx={{ flex: 1, p: 1, bgcolor: "#fff", minHeight: 280 }}>
          <Box
            sx={{
              display: "flex",
              gap: 1,
              mb: 1,
              justifyContent: "flex-end",
            }}
          >
            <Typography
              component="button"
              onClick={() => {
                setSelectedRefs(new Set());
                setActiveRef(null);
              }}
              sx={{
                border: "1px solid #ccc",
                bgcolor: "#f5f5f5",
                color: theme.palette.primary.main,
                px: 1.5,
                py: 0.5,
                fontSize: 12,
                cursor: "pointer",
                fontFamily: "Inter",
              }}
            >
              Clear
            </Typography>
          </Box>
          <TableContainer
            ref={listContainerRef}
            sx={{ maxHeight: 420 }}
          >
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" sx={{ width: 42 }} />
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: 12,
                      color: theme.palette.primary.main,
                      width: 56,
                    }}
                  >
                    REF.
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: 12,
                      color: theme.palette.primary.main,
                    }}
                  >
                    NUMBER
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleParts.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3}>
                      <Typography
                        variant="body2"
                        sx={{ color: theme.palette.text.primary }}
                      >
                        No OEM parts returned for this diagram.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {visibleParts.map((part, index) => {
                  const refRaw = partRef(part);
                  const refKey = normalizeOemRef(refRaw) || refRaw;
                  const oem = partNumber(part);
                  const name = partName(part);
                  const highlighted = isRowActive(refRaw);
                  return (
                    <TableRow
                      key={`${refKey}-${oem}-${index}`}
                      ref={(el) => {
                        rowRefs.current[refKey] = el;
                      }}
                      hover
                      onClick={() => {
                        activateRef(refRaw);
                        if (oem) onOemClick?.(oem);
                      }}
                      sx={{
                        cursor: "pointer",
                        bgcolor: highlighted
                          ? "rgba(237, 47, 66, 0.12)"
                          : "transparent",
                        "& td": {
                          borderBottomColor: "#eee",
                        },
                        "&.MuiTableRow-hover:hover": {
                          bgcolor: highlighted
                            ? "rgba(237, 47, 66, 0.18)"
                            : "rgba(0,0,0,0.04)",
                        },
                      }}
                    >
                      <TableCell
                        padding="checkbox"
                        sx={{
                          borderLeft: highlighted
                            ? `4px solid ${theme.palette.secondary.main}`
                            : "4px solid transparent",
                        }}
                      >
                        <Checkbox
                          size="small"
                          checked={selectedRefs.has(refKey)}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => toggleRef(refRaw)}
                          sx={{
                            color: theme.palette.secondary.main,
                            "&.Mui-checked": {
                              color: theme.palette.secondary.main,
                            },
                            p: 0.5,
                          }}
                        />
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: 12,
                          fontWeight: highlighted ? 700 : 500,
                          color: theme.palette.primary.main,
                        }}
                      >
                        {formatOemRef(refRaw)}
                      </TableCell>
                      <TableCell sx={{ fontSize: 12 }}>
                        <Box>
                          {name && (
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: 12,
                                mb: 0.25,
                                fontWeight: 600,
                                color: theme.palette.primary.main,
                              }}
                            >
                              {name}
                            </Typography>
                          )}
                          {oem ? (
                            <Link
                              component="button"
                              type="button"
                              underline="hover"
                              onClick={(e) => {
                                e.stopPropagation();
                                activateRef(refRaw);
                                onOemClick?.(oem);
                              }}
                              sx={{
                                color: theme.palette.primary.dark,
                                fontSize: 13,
                                fontWeight: 600,
                                fontFamily: "Inter",
                              }}
                            >
                              {oem}
                            </Link>
                          ) : (
                            "—"
                          )}
                          {part.replacedBy && (
                            <Typography
                              variant="caption"
                              display="block"
                              sx={{ color: theme.palette.text.primary }}
                            >
                              Replaced by → {String(part.replacedBy)}
                            </Typography>
                          )}
                          {part.note && (
                            <Typography
                              variant="caption"
                              display="block"
                              sx={{ color: theme.palette.text.primary }}
                            >
                              {String(part.note)}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
    </Paper>
  );
};

export default VinalfaOemDiagramSection;
